// Boolean Network Model for Somatic Cell Aging (Beyond Life prototype)
// Ported from the original vanilla-JS prototype into an ES module.

export const INPUT_NODES = [
  "NUTRIENTS", "GF", "DNA_DAMAGE", "ER_STRESS",
  "INFLAM_ENV", "HYPOXIA", "MATRIX", "OX_STRESS",
];

export const INTERNAL_NODES = [
  "RTK", "INTEGRIN", "SRC", "SYK", "LAT", "PLC", "PIP2", "IP3", "CA", "DAG", "PKC",
  "PI3K", "PDK1", "AKT", "SOS", "RAS", "AP1", "NFAT", "NFKB",
  "LKB1", "AMPK", "SIRTUIN", "MTOR", "MTORC1", "MTORC2", "HIF1A", "GLYCOLYSIS",
  "MITO_FUNC", "OXPHOS", "ROS", "NRF2", "FOXO", "AUTOPHAGY",
  "DDR", "P53", "P21", "P16_RB", "BCL2", "APOPTOSIS", "SENESCENCE", "SASP", "PROLIFERATION",
];

export const ALL_NODES = [...INPUT_NODES, ...INTERNAL_NODES];

export const SIGNALING_NODES = [
  "RTK", "INTEGRIN", "SRC", "SYK", "LAT", "PLC", "PIP2", "IP3", "CA", "DAG", "PKC",
  "PI3K", "PDK1", "AKT", "SOS", "RAS", "AP1", "NFAT", "NFKB",
];
export const METABOLIC_NODES = [
  "LKB1", "AMPK", "SIRTUIN", "MTOR", "MTORC1", "MTORC2", "HIF1A", "GLYCOLYSIS", "MITO_FUNC", "OXPHOS",
];
export const STRESS_NODES = [
  "ROS", "NRF2", "FOXO", "AUTOPHAGY", "DDR", "P53", "P21", "P16_RB", "BCL2",
];
export const FATE_NODES = ["APOPTOSIS", "SENESCENCE", "SASP", "PROLIFERATION"];

export const KEY_NODES = [
  "PROLIFERATION", "SENESCENCE", "APOPTOSIS", "SASP", "ROS", "DDR",
  "P53", "MTORC1", "AMPK", "SIRTUIN", "AUTOPHAGY",
];

export const EDGES = [
  ["GF","RTK"],["MATRIX","INTEGRIN"],["RTK","SRC"],["RTK","SYK"],["SRC","SYK"],["SYK","LAT"],
  ["SYK","PLC"],["PLC","PIP2"],["PIP2","IP3"],["IP3","CA"],["PLC","DAG"],["DAG","PKC"],
  ["RTK","PI3K"],["INTEGRIN","PI3K"],["RTK","PDK1"],["INTEGRIN","PDK1"],["PI3K","AKT"],["PDK1","AKT"],
  ["INTEGRIN","SOS"],["LAT","RAS"],["SOS","RAS"],["DAG","RAS"],["RAS","AP1"],["CA","NFAT"],
  ["PKC","NFKB"],["INFLAM_ENV","NFKB"],["ROS","NFKB"],["NUTRIENTS","LKB1"],["ER_STRESS","LKB1"],
  ["ROS","LKB1"],["HYPOXIA","LKB1"],["LKB1","AMPK"],["NUTRIENTS","AMPK"],["ER_STRESS","AMPK"],
  ["ROS","AMPK"],["HYPOXIA","AMPK"],["AMPK","SIRTUIN"],["NUTRIENTS","SIRTUIN"],["AKT","MTOR"],
  ["NUTRIENTS","MTOR"],["AMPK","MTOR"],["MTOR","MTORC1"],["AMPK","MTORC1"],["MTOR","MTORC2"],
  ["AMPK","MTORC2"],["HYPOXIA","HIF1A"],["MTORC1","HIF1A"],["AMPK","HIF1A"],["HIF1A","GLYCOLYSIS"],
  ["MTORC1","GLYCOLYSIS"],["NUTRIENTS","GLYCOLYSIS"],["ROS","MITO_FUNC"],["AUTOPHAGY","MITO_FUNC"],
  ["MITO_FUNC","OXPHOS"],["NUTRIENTS","OXPHOS"],["HYPOXIA","OXPHOS"],["OX_STRESS","ROS"],
  ["MITO_FUNC","ROS"],["INFLAM_ENV","ROS"],["NFKB","ROS"],["ROS","NRF2"],["APOPTOSIS","NRF2"],
  ["AMPK","FOXO"],["SIRTUIN","FOXO"],["AKT","FOXO"],["AMPK","AUTOPHAGY"],["FOXO","AUTOPHAGY"],
  ["NRF2","AUTOPHAGY"],["SIRTUIN","AUTOPHAGY"],["MTORC1","AUTOPHAGY"],["ER_STRESS","AUTOPHAGY"],
  ["DNA_DAMAGE","DDR"],["ROS","DDR"],["AUTOPHAGY","DDR"],["DDR","P53"],["P53","P21"],["DDR","P16_RB"],
  ["ROS","P16_RB"],["AKT","BCL2"],["P53","BCL2"],["P53","APOPTOSIS"],["DDR","APOPTOSIS"],
  ["ROS","APOPTOSIS"],["BCL2","APOPTOSIS"],["MTORC2","APOPTOSIS"],["P53","SENESCENCE"],
  ["DDR","SENESCENCE"],["ROS","SENESCENCE"],["MTORC1","SENESCENCE"],["INFLAM_ENV","SENESCENCE"],
  ["SENESCENCE","SASP"],["NFKB","SASP"],["RTK","PROLIFERATION"],["INTEGRIN","PROLIFERATION"],
  ["MTORC1","PROLIFERATION"],["GLYCOLYSIS","PROLIFERATION"],["P21","PROLIFERATION"],
  ["P16_RB","PROLIFERATION"],["APOPTOSIS","PROLIFERATION"],["SENESCENCE","PROLIFERATION"],
];

export const INPUT_DESCRIPTIONS = {
  NUTRIENTS: "Nutrient availability",
  GF: "Growth factors present",
  DNA_DAMAGE: "DNA damage signal",
  ER_STRESS: "ER stress signal",
  INFLAM_ENV: "Inflammatory environment",
  HYPOXIA: "Low oxygen condition",
  MATRIX: "Extracellular matrix contact",
  OX_STRESS: "Oxidative stress",
};

export const PRESETS = {
  proliferative: {
    name: "Proliferative",
    description: "Healthy cell with growth factors and nutrients, low stress.",
    inputs: { NUTRIENTS: true, GF: true, DNA_DAMAGE: false, ER_STRESS: false, INFLAM_ENV: false, HYPOXIA: false, MATRIX: true, OX_STRESS: false },
  },
  aging: {
    name: "Aging",
    description: "Chronic damage with an inflammatory environment.",
    inputs: { NUTRIENTS: true, GF: true, DNA_DAMAGE: true, ER_STRESS: true, INFLAM_ENV: true, HYPOXIA: false, MATRIX: true, OX_STRESS: true },
  },
  senescence: {
    name: "Senescence",
    description: "Pre-damaged state under oxidative stress.",
    inputs: { NUTRIENTS: true, GF: false, DNA_DAMAGE: false, ER_STRESS: false, INFLAM_ENV: false, HYPOXIA: false, MATRIX: true, OX_STRESS: true },
  },
  apoptosis: {
    name: "Apoptosis",
    description: "Severe damage leading to programmed cell death.",
    inputs: { NUTRIENTS: false, GF: false, DNA_DAMAGE: true, ER_STRESS: true, INFLAM_ENV: false, HYPOXIA: true, MATRIX: false, OX_STRESS: true },
  },
};

export function makeInitialState(inputValues, defaultInternal = false) {
  const state = {};
  for (const node of INPUT_NODES) state[node] = Boolean(inputValues[node]);
  for (const node of INTERNAL_NODES) state[node] = defaultInternal;
  return state;
}

export function stateToTuple(state) {
  return ALL_NODES.map((n) => (state[n] ? 1 : 0));
}

export function updateState(state, forced = null) {
  const s = state;
  const newState = {};
  for (const node of INPUT_NODES) newState[node] = s[node];

  const NUTRIENTS = s.NUTRIENTS, GF = s.GF, DNA_DAMAGE = s.DNA_DAMAGE, ER_STRESS = s.ER_STRESS,
    INFLAM_ENV = s.INFLAM_ENV, HYPOXIA = s.HYPOXIA, MATRIX = s.MATRIX, OX_STRESS = s.OX_STRESS;
  const prev_ROS = s.ROS, prev_AUTOPHAGY = s.AUTOPHAGY, prev_APOPTOSIS = s.APOPTOSIS,
    prev_SENESCENCE = s.SENESCENCE, prev_SASP = s.SASP;

  const F = forced || {};
  const c = (n, v) => (n in F ? F[n] : v); // clamp forced nodes mid-cascade

  const RTK = c("RTK", GF), INTEGRIN = c("INTEGRIN", MATRIX), SRC = c("SRC", RTK),
    SYK = c("SYK", RTK && SRC), LAT = c("LAT", SYK), PLC = c("PLC", SYK),
    PIP2 = c("PIP2", PLC), IP3 = c("IP3", PIP2), CA = c("CA", IP3), DAG = c("DAG", PLC), PKC = c("PKC", DAG);
  const PI3K = c("PI3K", RTK || INTEGRIN), PDK1 = c("PDK1", RTK || INTEGRIN), AKT = c("AKT", PI3K || PDK1);
  const SOS = c("SOS", INTEGRIN), RAS = c("RAS", LAT && SOS && DAG), AP1 = c("AP1", RAS), NFAT = c("NFAT", CA);
  const NFKB = c("NFKB", PKC || INFLAM_ENV || prev_ROS);
  const LKB1 = c("LKB1", (!NUTRIENTS) || ER_STRESS || prev_ROS || HYPOXIA);
  const AMPK = c("AMPK", LKB1 && ((!NUTRIENTS) || ER_STRESS || prev_ROS || HYPOXIA));
  const SIRTUIN = c("SIRTUIN", AMPK && !NUTRIENTS);
  const MTOR = c("MTOR", AKT && NUTRIENTS && !AMPK);
  const MTORC1 = c("MTORC1", MTOR && !AMPK), MTORC2 = c("MTORC2", MTOR && AMPK);
  const HIF1A = c("HIF1A", (HYPOXIA || MTORC1) && !AMPK);
  const GLYCOLYSIS = c("GLYCOLYSIS", (HIF1A || MTORC1) && NUTRIENTS);
  const MITO_FUNC = c("MITO_FUNC", (!prev_ROS) || prev_AUTOPHAGY);
  const OXPHOS = c("OXPHOS", MITO_FUNC && NUTRIENTS && !HYPOXIA);
  const ROS = c("ROS", OX_STRESS || (!MITO_FUNC) || (INFLAM_ENV && NFKB));
  const NRF2 = c("NRF2", ROS && !prev_APOPTOSIS);
  const FOXO = c("FOXO", (AMPK || SIRTUIN) && !AKT);
  const AUTOPHAGY = c("AUTOPHAGY", (AMPK || FOXO || NRF2 || SIRTUIN) && !MTORC1 && !ER_STRESS);
  const DDR = c("DDR", DNA_DAMAGE || (ROS && !AUTOPHAGY));
  const P53 = c("P53", DDR);
  const P21 = c("P21", P53), P16_RB = c("P16_RB", DDR && ROS);
  const BCL2 = c("BCL2", AKT && !P53);
  const APOPTOSIS = c("APOPTOSIS", (P53 && (DDR || ROS) && !BCL2 && !MTORC2) || (prev_APOPTOSIS && P53));
  const SENESCENCE = c("SENESCENCE", (P53 && (DDR || ROS) && MTORC1) || (prev_SENESCENCE && (DDR || ROS || INFLAM_ENV)));
  const SASP = c("SASP", (SENESCENCE && NFKB) || (prev_SASP && SENESCENCE));
  const PROLIFERATION = c("PROLIFERATION", RTK && INTEGRIN && MTORC1 && GLYCOLYSIS && (!P21) && (!P16_RB) && (!APOPTOSIS) && (!SENESCENCE));

  Object.assign(newState, {
    RTK, INTEGRIN, SRC, SYK, LAT, PLC, PIP2, IP3, CA, DAG, PKC, PI3K, PDK1, AKT, SOS, RAS, AP1,
    NFAT, NFKB, LKB1, AMPK, SIRTUIN, MTOR, MTORC1, MTORC2, HIF1A, GLYCOLYSIS, MITO_FUNC, OXPHOS,
    ROS, NRF2, FOXO, AUTOPHAGY, DDR, P53, P21, P16_RB, BCL2, APOPTOSIS, SENESCENCE, SASP, PROLIFERATION,
  });
  return newState;
}

export function simulate(initialState, maxSteps = 100, forced = null) {
  const history = [];
  const seen = new Map();
  let state = { ...initialState };
  if (forced) for (const node in forced) state[node] = forced[node];
  for (let t = 0; t < maxSteps; t++) {
    const key = stateToTuple(state).join(",");
    if (seen.has(key)) {
      const start = seen.get(key);
      return { history, attractor: history.slice(start), attractorStart: start };
    }
    seen.set(key, t);
    history.push({ ...state });
    state = updateState(state, forced);
  }
  return { history, attractor: [], attractorStart: null };
}

export function classifyFate(state) {
  if (!state) return "Undetermined";
  const on = [];
  if (state.PROLIFERATION) on.push("Proliferation");
  if (state.APOPTOSIS) on.push("Apoptosis");
  if (state.SENESCENCE) on.push(state.SASP ? "Senescence + SASP" : "Senescence");
  if (on.length === 0) return "Arrested / adaptive";
  return on.join(" + ");
}

export function sampleAttractors(inputValues, forced = null, nSamples = 40, maxSteps = 60) {
  const counts = new Map();
  const fates = new Map();
  for (let i = 0; i < nSamples; i++) {
    const init = {};
    for (const node of INPUT_NODES) init[node] = Boolean(inputValues[node]);
    for (const node of INTERNAL_NODES) init[node] = Math.random() < 0.5;
    const { attractor } = simulate(init, maxSteps, forced);
    if (!attractor.length) continue;
    const rep = stateToTuple(attractor[0]).join(",");
    counts.set(rep, (counts.get(rep) || 0) + 1);
    const fate = classifyFate(attractor[0]);
    fates.set(fate, (fates.get(fate) || 0) + 1);
  }
  return {
    totalSamples: nSamples,
    distinctAttractors: counts.size,
    fateDistribution: Array.from(fates.entries()).map(([fate, count]) => ({ fate, count })).sort((a, b) => b.count - a.count),
  };
}

export function getNodeCategory(node) {
  if (INPUT_NODES.includes(node)) return "input";
  if (SIGNALING_NODES.includes(node)) return "signaling";
  if (METABOLIC_NODES.includes(node)) return "metabolic";
  if (STRESS_NODES.includes(node)) return "stress";
  if (FATE_NODES.includes(node)) return "fate";
  return "unknown";
}

// Deterministic column layout by category for network rendering.
const COLUMNS = [
  { cat: "input", nodes: INPUT_NODES },
  { cat: "signaling", nodes: SIGNALING_NODES },
  { cat: "metabolic", nodes: METABOLIC_NODES },
  { cat: "stress", nodes: STRESS_NODES },
  { cat: "fate", nodes: FATE_NODES },
];

export function computeLayout(width, height) {
  const pos = {};
  const padX = 60, padY = 40;
  const usableW = width - padX * 2;
  COLUMNS.forEach((col, ci) => {
    const x = padX + (usableW * ci) / (COLUMNS.length - 1);
    const n = col.nodes.length;
    col.nodes.forEach((node, i) => {
      const y = padY + ((height - padY * 2) * (i + 0.5)) / n;
      pos[node] = { x, y, cat: col.cat };
    });
  });
  return pos;
}

export const CATEGORY_LABELS = {
  input: "Inputs",
  signaling: "Signaling",
  metabolic: "Metabolic",
  stress: "Stress & repair",
  fate: "Cell fate",
};
